import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, ArrowLeft, Loader2, MessageSquare, ChevronLeft, ChevronRight, History } from "lucide-react";
import toast from "react-hot-toast";
import { formatAnomalyDetail, type AnomalyHit } from "../utils/timesheetAnomalies";

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...init,
  });
  if (res.status === 204) return {} as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.message || "Request failed"), data);
  return data as T;
}

interface QueueRow {
  id: string;
  teamMemberId: string;
  teamMemberName: string;
  periodStart: string;
  periodEnd: string;
  omRouteReason: "anomaly" | "appeal";
  anomalyFlags: string[];
}

interface ResolvedRow {
  id: string;
  teamMemberId: string;
  teamMemberName: string;
  periodStart: string;
  periodEnd: string;
  omRouteReason: "anomaly" | "appeal";
  omReviewedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface DayEntry {
  workDate: string; clockInAt: string | null; clockOutAt: string | null; breakMinutes: number; hours: number | null;
}

interface Adjustment {
  id: string;
  workDate: string;
  field: string;
  requestedValue: string;
  reason: string;
  teamLeadDecision: "approved" | "edited" | "denied" | null;
  teamLeadOverrideValue: string | null;
  teamLeadNote: string | null;
  pccConfirmationStatus: "pending" | "confirmed" | "appealed" | null;
  pccAppealReason: string | null;
  omDecision: "sided_with_team_lead" | "sided_with_pcc" | "overridden" | null;
}

interface AuditEvent {
  id: string;
  eventType: string;
  actorRole: string;
  note: string | null;
  valueBefore: string | null;
  valueAfter: string | null;
  createdAt: string;
}

interface PeriodDetail {
  period: { id: string; periodStart: string; periodEnd: string; omRouteReason: "anomaly" | "appeal"; anomalyDetails: unknown };
  live: { days: DayEntry[]; totalHours: number };
  adjustments: Adjustment[];
  auditTrail: AuditEvent[];
}

const FIELD_LABELS: Record<string, string> = {
  clock_in: "Clock In", clock_out: "Clock Out", break_start: "Break Start", break_end: "Break End",
};
const EVENT_LABELS: Record<string, string> = {
  pcc_requested_edit: "PCC requested this correction",
  team_lead_approved: "Team Lead approved",
  team_lead_edited: "Team Lead edited",
  team_lead_denied: "Team Lead denied",
  pcc_confirmed: "PCC confirmed the edit",
  pcc_appealed: "PCC appealed",
  om_sided_with_team_lead: "OM sided with Team Lead",
  om_sided_with_pcc: "OM sided with PCC",
  om_overridden: "OM overrode with a new value",
  om_overrode_anomaly: "OM overrode the flagged anomaly",
  period_finalized: "Period finalized",
};

function describeAnomaly(hit: AnomalyHit): string {
  return `${formatDate(hit.workDate)} — ${formatAnomalyDetail(hit)}`;
}

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function AppealResolveCard({ adj, periodId, onResolved }: { adj: Adjustment; periodId: string; onResolved: () => void }) {
  const [mode, setMode] = useState<"view" | "override">("view");
  const [overrideValue, setOverrideValue] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resolve = async (decision: "sided_with_team_lead" | "sided_with_pcc" | "overridden") => {
    if (!note.trim() && decision !== "overridden") {
      // still required by the API even for the non-override decisions — collect it inline
    }
    if (!note.trim()) {
      toast.error("A note is required for every resolution");
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest(`/api/v1/timesheet-review/om/${periodId}/resolve`, {
        method: "POST",
        body: JSON.stringify({
          resolutions: [{
            adjustmentId: adj.id, decision, note,
            ...(decision === "overridden" ? { overrideValue: new Date(overrideValue).toISOString() } : {}),
          }],
        }),
      });
      toast.success("Resolved");
      onResolved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resolve");
    } finally {
      setSubmitting(false);
    }
  };

  if (adj.omDecision) {
    return (
      <div className="border border-gray-100 rounded-xl p-3 bg-gray-50">
        <p className="text-sm font-medium text-gray-800">{formatDate(adj.workDate)} — {FIELD_LABELS[adj.field] ?? adj.field}</p>
        <p className="text-xs text-emerald-700 mt-1">Resolved: {adj.omDecision.replace(/_/g, " ")}</p>
      </div>
    );
  }

  return (
    <div className="border border-red-100 rounded-xl p-3 space-y-2">
      <p className="text-sm font-medium text-gray-800">{formatDate(adj.workDate)} — {FIELD_LABELS[adj.field] ?? adj.field}</p>
      <div className="text-xs text-gray-500 space-y-1">
        <p>PCC requested: <span className="font-medium text-gray-700">{formatTime(adj.requestedValue)}</span></p>
        <p>Team Lead's correction: <span className="font-medium text-blue-700">{formatTime(adj.teamLeadOverrideValue)}</span> — "{adj.teamLeadNote}"</p>
        <p>PCC's appeal: <span className="italic">"{adj.pccAppealReason}"</span></p>
      </div>

      {mode === "view" ? (
        <>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note explaining your decision (required)" rows={2}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => resolve("sided_with_team_lead")} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">Side with Team Lead</button>
            <button onClick={() => resolve("sided_with_pcc")} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Side with PCC</button>
            <button onClick={() => setMode("override")} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Override</button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <input type="datetime-local" value={overrideValue} onChange={(e) => setOverrideValue(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note explaining your decision (required)" rows={2}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full" />
          <div className="flex gap-2">
            <button onClick={() => resolve("overridden")} disabled={submitting || !overrideValue || !note.trim()} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50">Confirm Override</button>
            <button onClick={() => setMode("view")} className="px-3 py-1.5 text-xs text-gray-500">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function TimesheetReviewOm() {
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PeriodDetail | null>(null);
  const [resolvingAnomaly, setResolvingAnomaly] = useState(false);
  const [anomalyOverrideMode, setAnomalyOverrideMode] = useState(false);
  const [anomalyOverrideNote, setAnomalyOverrideNote] = useState("");

  const [resolved, setResolved] = useState<ResolvedRow[]>([]);
  const [resolvedPagination, setResolvedPagination] = useState<Pagination | null>(null);
  const [resolvedPage, setResolvedPage] = useState(1);
  const [resolvedLoading, setResolvedLoading] = useState(true);

  const loadQueue = () => {
    setLoading(true);
    apiRequest<{ data: QueueRow[] }>("/api/v1/timesheet-review/om/queue")
      .then((res) => setQueue(res.data))
      .catch(() => toast.error("Failed to load queue"))
      .finally(() => setLoading(false));
  };

  const loadResolved = (page: number) => {
    setResolvedLoading(true);
    apiRequest<{ data: ResolvedRow[]; pagination: Pagination }>(`/api/v1/timesheet-review/om/resolved?page=${page}&limit=10`)
      .then((res) => { setResolved(res.data); setResolvedPagination(res.pagination); })
      .catch(() => toast.error("Failed to load resolved issues"))
      .finally(() => setResolvedLoading(false));
  };

  useEffect(loadQueue, []);
  useEffect(() => loadResolved(resolvedPage), [resolvedPage]);

  const openPeriod = (id: string) => {
    setSelectedId(id);
    apiRequest<{ data: PeriodDetail }>(`/api/v1/timesheet-review/om/${id}`)
      .then((res) => setDetail(res.data))
      .catch(() => toast.error("Failed to load period"));
  };

  const refreshDetail = () => {
    if (selectedId) openPeriod(selectedId);
    // Harmless if the period isn't fully resolved yet (only the last appealed adjustment on a
    // period actually flips it to om_approved) -- just refreshes to the same list until it does.
    loadResolved(1);
    setResolvedPage(1);
  };

  const resolveAnomaly = async (decision: "approve" | "override") => {
    if (!selectedId) return;
    if (decision === "override" && !anomalyOverrideNote.trim()) {
      toast.error("A note is required to override an anomaly");
      return;
    }
    setResolvingAnomaly(true);
    try {
      await apiRequest(`/api/v1/timesheet-review/om/${selectedId}/resolve`, {
        method: "POST",
        body: JSON.stringify({ decision, ...(decision === "override" ? { note: anomalyOverrideNote } : {}) }),
      });
      toast.success("Approved — timesheet fully finalized");
      setSelectedId(null);
      setDetail(null);
      setAnomalyOverrideMode(false);
      setAnomalyOverrideNote("");
      loadQueue();
      loadResolved(1);
      setResolvedPage(1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to approve");
    } finally {
      setResolvingAnomaly(false);
    }
  };

  const unresolvedAppeals = detail?.adjustments.filter((a) => a.pccConfirmationStatus === "appealed" && !a.omDecision) ?? [];

  if (selectedId && detail) {
    return (
      <div className="space-y-5">
        <button onClick={() => { setSelectedId(null); setDetail(null); setAnomalyOverrideMode(false); setAnomalyOverrideNote(""); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-4 w-4" /> Back to queue
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">
              {formatDate(detail.period.periodStart)} – {formatDate(detail.period.periodEnd)}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5 capitalize">Routed for: {detail.period.omRouteReason}</p>
          </div>
          <div className="divide-y divide-gray-50">
            {detail.live.days.map((d) => (
              <div key={d.workDate} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">{formatDate(d.workDate)}</p>
                  <p className="text-xs text-gray-400">{formatTime(d.clockInAt)} – {formatTime(d.clockOutAt)}</p>
                </div>
                <span className="text-sm font-semibold text-gray-700">{d.hours != null ? `${d.hours.toFixed(2)}h` : "—"}</span>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between border-t border-gray-100">
            <span className="text-sm font-semibold text-gray-600">Total</span>
            <span className="text-sm font-bold text-gray-900">{detail.live.totalHours.toFixed(2)}h</span>
          </div>
        </div>

        {detail.period.omRouteReason === "anomaly" ? (
          <div className="max-w-xl bg-amber-50 border border-amber-100 rounded-2xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> Anomaly flagged
            </h3>
            <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
              {(Array.isArray(detail.period.anomalyDetails) ? (detail.period.anomalyDetails as AnomalyHit[]) : []).map((hit, i) => (
                <li key={i}>{describeAnomaly(hit)}</li>
              ))}
            </ul>

            {!anomalyOverrideMode ? (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => resolveAnomaly("approve")}
                  disabled={resolvingAnomaly}
                  className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {resolvingAnomaly ? "Approving…" : "Approve — finalize this timesheet"}
                </button>
                <button
                  onClick={() => setAnomalyOverrideMode(true)}
                  disabled={resolvingAnomaly}
                  className="w-full px-4 py-2 text-sm font-medium rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                >
                  Override this anomaly
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-amber-700">
                  Explain why this flagged hours should be waived (e.g. pre-approved overtime for a special project). This will be recorded on the timesheet's history.
                </p>
                <textarea
                  value={anomalyOverrideNote}
                  onChange={(e) => setAnomalyOverrideNote(e.target.value)}
                  placeholder="Reason for overriding this anomaly (required)"
                  rows={2}
                  className="text-xs border border-amber-200 rounded-lg px-2 py-1.5 w-full"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => resolveAnomaly("override")}
                    disabled={resolvingAnomaly || !anomalyOverrideNote.trim()}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
                  >
                    {resolvingAnomaly ? "Submitting…" : "Confirm Override"}
                  </button>
                  <button onClick={() => { setAnomalyOverrideMode(false); setAnomalyOverrideNote(""); }} className="px-3 py-1.5 text-xs text-amber-700">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="max-w-xl bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-3">
                <MessageSquare className="h-4 w-4" /> Full history
              </h3>
              <div className="space-y-2">
                {detail.auditTrail.map((e) => (
                  <div key={e.id} className="text-xs border-l-2 border-gray-200 pl-3 py-0.5">
                    <p className="text-gray-700 font-medium">{EVENT_LABELS[e.eventType] ?? e.eventType} <span className="text-gray-400 font-normal">· {e.actorRole}</span></p>
                    {e.note && <p className="text-gray-500 italic">"{e.note}"</p>}
                    <p className="text-gray-300">{formatDateTime(e.createdAt)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="max-w-xl space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">Disputed corrections ({unresolvedAppeals.length} unresolved)</h3>
              {detail.adjustments.filter((a) => a.pccConfirmationStatus === "appealed" || a.omDecision).map((adj) => (
                <AppealResolveCard key={adj.id} adj={adj} periodId={selectedId} onResolved={refreshDetail} />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-blue-500" /> Timesheet Review — Operations Manager
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">Anomalies and appeals escalated for your final decision.</p>
      </div>

      <div className="flex gap-5 items-start flex-wrap">
        <div className="flex-1 min-w-[320px] max-w-2xl">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400 py-10 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : queue.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
              <ShieldCheck className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Nothing escalated to you right now.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="divide-y divide-gray-50">
                {queue.map((row) => (
                  <button key={row.id} onClick={() => openPeriod(row.id)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 text-left">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{row.teamMemberName}</p>
                      <p className="text-xs text-gray-400">{formatDate(row.periodStart)} – {formatDate(row.periodEnd)}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${row.omRouteReason === "appeal" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      {row.omRouteReason}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Resolved Timesheet Issues — right-side history panel, paginated since this grows
            indefinitely unlike the live queue above. */}
        <div className="w-full sm:w-80 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <History className="h-4 w-4 text-gray-400" /> Resolved Timesheet Issues
            </h2>
          </div>

          {resolvedLoading ? (
            <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : resolved.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">Nothing resolved yet.</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {resolved.map((row) => (
                <div key={row.id} className="px-4 py-2.5">
                  <p className="text-sm font-medium text-gray-800">{row.teamMemberName}</p>
                  <p className="text-xs text-gray-400">{formatDate(row.periodStart)} – {formatDate(row.periodEnd)}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize ${row.omRouteReason === "appeal" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                      {row.omRouteReason}
                    </span>
                    <span className="text-[10px] text-gray-300">{formatDateTime(row.omReviewedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {resolvedPagination && resolvedPagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
              <button
                onClick={() => setResolvedPage((p) => Math.max(1, p - 1))}
                disabled={resolvedPage <= 1}
                className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="text-[11px] text-gray-400">
                Page {resolvedPagination.page} of {resolvedPagination.totalPages}
              </span>
              <button
                onClick={() => setResolvedPage((p) => Math.min(resolvedPagination.totalPages, p + 1))}
                disabled={resolvedPage >= resolvedPagination.totalPages}
                className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
